import { appConfig } from "@/config/appConfig";
import { getAuthToken } from "@/api/client";

/**
 * The user's current lens, forwarded to the backend so Claude can scope answers to what they're
 * looking at (unless they broaden the question). `activeFilters` is an arbitrary JSON snapshot of
 * the screen's URL/filter state.
 */
export interface ChatContext {
  currentScreen?: string;
  activeFilters?: unknown;
  /** What the panel says it answers for ("Whole org", "Erica Heaslip's team"), 2026-10-29. */
  scope?: string;
  /** The open drawer or tile, in the tools' names. */
  drill?: unknown;
  /** The record on a detail screen. */
  recordId?: string;
  /** When the data on screen was loaded (UTC ISO). */
  dataAsOfUtc?: string;
}

export interface ChatStreamRequest {
  sessionId: string;
  message: string;
  context?: ChatContext;
}

export interface TurnUsage {
  inputTokens: number;
  outputTokens: number;
}

/** Normalized SSE events from POST /api/assistant/chat/stream (mirrors the backend union). */
export type AssistantStreamEvent =
  | { type: "token"; text: string }
  | { type: "tool_call"; name: string; input: string }
  | { type: "tool_result"; name: string }
  | { type: "done"; usage: TurnUsage }
  | { type: "error"; message: string };

interface StreamHandlers {
  onEvent: (event: AssistantStreamEvent) => void;
  signal?: AbortSignal;
}

/**
 * Opens the SSE chat stream and invokes `onEvent` for each parsed frame. Uses raw `fetch` +
 * `ReadableStream` (not `EventSource`) so we can send the `Authorization` header; deliberately
 * bypasses `apiFetch` (which calls `response.json()`). Non-OK responses — notably 429 rate-limit —
 * are surfaced as a single `error` event rather than thrown, so the caller has one code path.
 */
export async function streamChat(
  request: ChatStreamRequest,
  { onEvent, signal }: StreamHandlers,
): Promise<void> {
  const url = `${appConfig.apiBaseUrl}/assistant/chat/stream`;
  let response: Response;
  try {
    const token = await getAuthToken();
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
      Accept: "text/event-stream",
    };
    if (token) headers.Authorization = `Bearer ${token}`;
    response = await fetch(url, {
      method: "POST",
      headers,
      body: JSON.stringify(request),
      signal,
    });
  } catch (err) {
    if (isAbort(err)) return;
    onEvent({ type: "error", message: "Couldn't reach the assistant. Check your connection and try again." });
    return;
  }

  if (!response.ok || !response.body) {
    onEvent({ type: "error", message: await errorMessageFor(response) });
    return;
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      // SSE frames are separated by a blank line. Process every complete frame in the buffer.
      let sep = buffer.indexOf("\n\n");
      while (sep !== -1) {
        const frame = buffer.slice(0, sep);
        buffer = buffer.slice(sep + 2);
        const event = parseFrame(frame);
        if (event) onEvent(event);
        sep = buffer.indexOf("\n\n");
      }
    }
  } catch (err) {
    if (isAbort(err)) return;
    onEvent({ type: "error", message: "The response was interrupted. Please try again." });
  }
}

/** Parses one `event: <type>\ndata: <json>` SSE frame into a typed event (null if unparseable). */
export function parseFrame(frame: string): AssistantStreamEvent | null {
  let eventType: string | null = null;
  const dataLines: string[] = [];
  for (const line of frame.split("\n")) {
    if (line.startsWith("event:")) {
      eventType = line.slice("event:".length).trim();
    } else if (line.startsWith("data:")) {
      dataLines.push(line.slice("data:".length).trim());
    }
  }
  if (!eventType || dataLines.length === 0) return null;

  let data: Record<string, unknown>;
  try {
    data = JSON.parse(dataLines.join("\n")) as Record<string, unknown>;
  } catch {
    return null;
  }

  switch (eventType) {
    case "token":
      return { type: "token", text: String(data.text ?? "") };
    case "tool_call":
      return { type: "tool_call", name: String(data.name ?? ""), input: String(data.input ?? "") };
    case "tool_result":
      return { type: "tool_result", name: String(data.name ?? "") };
    case "done": {
      const usage = (data.usage ?? {}) as Record<string, unknown>;
      return {
        type: "done",
        usage: {
          inputTokens: Number(usage.inputTokens ?? 0),
          outputTokens: Number(usage.outputTokens ?? 0),
        },
      };
    }
    case "error":
      return { type: "error", message: String(data.message ?? "Something went wrong.") };
    default:
      return null;
  }
}

function isAbort(err: unknown): boolean {
  return err instanceof DOMException && err.name === "AbortError";
}

async function errorMessageFor(response: Response): Promise<string> {
  if (response.status === 429) {
    // The server sends { error } JSON for rate-limit rejections before any SSE.
    const fallback = "You've reached the assistant's usage limit for now. Please try again later.";
    try {
      const body = (await response.json()) as { error?: string };
      return typeof body?.error === "string" && body.error ? body.error : fallback;
    } catch {
      return fallback;
    }
  }
  if (response.status === 401 || response.status === 403) {
    return "Your session has expired. Refresh the page and sign in again.";
  }
  return `The assistant is unavailable right now (error ${response.status}). Please try again.`;
}
